import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { getLocaleProfile } from '../../../shared/config/localeProfile';
import { BRAND } from '../../../shared/config/brand';
import { FormField } from '../../../shared/ui/FormField';
import { MetadataEditor, MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CaptureFieldsEditor, CaptureField } from '../../../shared/ui/CaptureFieldsEditor';
import { SettingsIcon, BuildingIcon, PaletteIcon, CreditCardIcon, UsersIcon, ScrollTextIcon, BellIcon, ShieldIcon, FormInputIcon, PercentIcon, ShoppingBagIcon, PackageIcon, InfoIcon, TagIcon, DollarSignIcon, ClockIcon, UploadIcon, PlusIcon, TrashIcon, MailIcon, CheckCircleIcon, WalletIcon, LockIcon, AlertCircleIcon, FileTextIcon, MoreHorizontalIcon, ActivityIcon, SearchIcon, DownloadIcon, RefreshCwIcon, ChevronDownIcon, ChevronUpIcon, ClipboardListIcon } from 'lucide-react';
type SettingsTab = 'company' | 'branding' | 'users' | 'payout' | 'general' | 'payment-providers' | 'receipt' | 'fields' | 'notifications' | 'security' | 'subscription-plan';
interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: any;
  category: 'account' | 'payment' | 'ecommerce' | 'system';
}
interface ActivityLog {
  id: string;
  timestamp: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  action: string;
  section: string;
  description: string;
  objectId: string;
  details: {
    amount?: string;
  };
  ipAddress: string;
  userAgent: string;
  status: 'success' | 'warning' | 'error' | 'info';
}
export function Settings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>('company');
  const currency = getTenant().currency.code;
  // ---- State ----
  const [companyInfo, setCompanyInfo] = useState({
    merchantId: 'MBNK-MY-001234',
    businessName: 'Bila Bila Mart',
    registrationNumber: '202301234567',
    gstNumber: 'MY123456789',
    businessEmail: 'billing@bilabilamart.my',
    businessPhone: '+60 3-1234 5678',
    language: 'en-US'
  });
  const [brandingSettings, setBrandingSettings] = useState({
    logoUrl: BRAND.logoUrl,
    primaryColor: BRAND.colors.primary,
    accentColor: '#2563eb'
  });
  const [generalSettings, setGeneralSettings] = useState({
    defaultExpiryDays: 7,
    redirectUrl: 'https://bilabilamart.my/payment-success',
    enableAutoReminders: true
  });
  const [receiptSettings, setReceiptSettings] = useState({
    showCompanyAddress: true,
    companyAddress: 'Lot 12, Jalan Sultan Ismail\nKuala Lumpur 50250\nMalaysia',
    ccEmail: 'receipts@bilabilamart.my',
    footerText: 'Thank you for your business!'
  });
  const [paymentMethods, setPaymentMethods] = useState([{
    id: 'visa',
    name: 'Visa',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'mastercard',
    name: 'Mastercard',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'amex',
    name: 'American Express',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'fpx',
    name: 'FPX (Online Banking)',
    enabled: true,
    icon: BuildingIcon,
    category: 'banking'
  }, {
    id: 'tng',
    name: "Touch 'n Go eWallet",
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'grabpay',
    name: 'GrabPay',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'duitnow',
    name: 'DuitNow QR',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'boost',
    name: 'Boost',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'shopeepay',
    name: 'ShopeePay',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }]);
  const [users, setUsers] = useState([{
    id: '1',
    name: 'Ahmed Lee',
    email: 'ahmed@bilabilamart.my',
    role: 'Owner',
    status: 'Active'
  }, {
    id: '2',
    name: 'Aishath Tan',
    email: 'aishath@bilabilamart.my',
    role: 'Finance',
    status: 'Active'
  }, {
    id: '3',
    name: 'Ibrahim Rahman',
    email: 'ibrahim@bilabilamart.my',
    role: 'Operations',
    status: 'Active'
  }]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showAllInvitePerms, setShowAllInvitePerms] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Operations');
  const [showRoleManagementModal, setShowRoleManagementModal] = useState(false);
  const [openUserMenuId, setOpenUserMenuId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<{
    id: string;
    name: string;
    email: string;
    role: string;
  } | null>(null);
  // Role definitions with permissions
  const rolePermissions = {
    Owner: {
      description: 'Full access to all features and settings',
      permissions: {
        Dashboard: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Payments: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Users: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Reports: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        }
      }
    },
    'Merchant Management': {
      description: 'Create/edit/view merchant accounts, KYC, onboarding',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    'Account Management': {
      description: 'View and manage existing merchant accounts, support actions, no financial controls',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Sales: {
      description: 'View merchant pipeline, create leads, view commercial terms, no settlement/finance access',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: false,
          insert: true,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Finance: {
      description: 'View settlements, payouts, MDR, invoices, refunds; no merchant onboarding rights',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: true
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: true,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Operations: {
      description: 'Transaction monitoring, dispute handling, reconciliation, basic merchant edits',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    }
  };
  const [termsSettings, setTermsSettings] = useState({
    termsText: 'By using our payment services, you agree to our terms and conditions.',
    privacyPolicy: 'We collect and process payment information in accordance with Malaysian data protection regulations.'
  });
  const [presetMetadataFields, setPresetMetadataFields] = useState<MetadataItem[]>([{
    key: 'customer_email',
    value: '',
    isPreset: true,
    required: true,
    type: 'email',
    description: "Customer's email address for receipts"
  }, {
    key: 'invoice_number',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Reference invoice number'
  }, {
    key: 'order_id',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Internal order ID'
  }]);
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    paymentReceived: true,
    paymentFailed: true,
    refundProcessed: true,
    disputeOpened: true,
    settlementCompleted: true,
    lowBalance: true
  });
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: true,
    sessionTimeout: 30,
    ipWhitelist: '',
    apiKeyVisible: false
  });
  const [selectedShop, setSelectedShop] = useState('Beach Shop');
  const [shopSettings, setShopSettings] = useState({
    inventoryTracking: true,
    lowStockThreshold: 5,
    notifyOnLowStock: false,
    allowBackorders: false,
    hideOutOfStock: false,
    enableTaxCalculation: false,
    pricesIncludeTax: false,
    processingFee: 0,
    shippingFee: 0
  });
  const [taxRates, setTaxRates] = useState([{
    id: '1',
    name: 'Standard Rate',
    rate: 6,
    isDefault: true
  }, {
    id: '2',
    name: 'Reduced Rate',
    rate: 3,
    isDefault: false
  }, {
    id: '3',
    name: 'Zero Rate',
    rate: 0,
    isDefault: false
  }]);
  const [customerFields, setCustomerFields] = useState([{
    id: '1',
    name: 'Full Name',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '2',
    name: 'Email',
    type: 'Email',
    required: true,
    enabled: true
  }, {
    id: '3',
    name: 'Phone Number',
    type: 'Phone',
    required: true,
    enabled: true
  }, {
    id: '4',
    name: 'Address',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '5',
    name: 'Additional Notes',
    type: 'Textarea',
    required: false,
    enabled: true
  }]);
  const [orderSettings, setOrderSettings] = useState({
    orderPrefix: 'ORD-',
    startingNumber: 1000
  });
  // Payout settings state
  const [minBalanceEnabled, setMinBalanceEnabled] = useState(false);
  const [minBalanceAmount, setMinBalanceAmount] = useState('');
  const [payoutDescriptor, setPayoutDescriptor] = useState('');
  const [shopFees, setShopFees] = useState([{
    id: '1',
    name: 'Processing Fee',
    type: 'Percentage',
    amount: 2.5,
    condition: 'Applied to all orders'
  }, {
    id: '2',
    name: 'Shipping Fee',
    type: 'Fixed',
    amount: 5.0,
    condition: 'Applied to orders over MYR 100'
  }]);
  const [shopPayoutSettings, setShopPayoutSettings] = useState({
    bankAccount: 'BML - 773000000001',
    frequency: 'Weekly',
    minimumAmount: 1000
  });
  const [shopNotifications, setShopNotifications] = useState({
    confirmationMessage: 'Thank you for your order! We will process it shortly.',
    notificationEmail: 'notifications@bilabilamart.my',
    notificationPhone: '+60 3-1234 5678',
    sendOrderNotifications: true,
    sendLowStockAlerts: false
  });
  // Activity Logs state
  const [activityLogs] = useState(() => {
    const logs = [];
    const now = new Date();
    const actions = [{
      type: 'created',
      section: 'Payments',
      desc: 'Payment created'
    }, {
      type: 'updated',
      section: 'Payments',
      desc: 'Payment updated'
    }, {
      type: 'deleted',
      section: 'Payments',
      desc: 'Payment deleted'
    }, {
      type: 'created',
      section: 'Customers',
      desc: 'Customer created'
    }, {
      type: 'updated',
      section: 'Customers',
      desc: 'Customer updated'
    }, {
      type: 'deleted',
      section: 'Customers',
      desc: 'Customer deleted'
    }, {
      type: 'created',
      section: 'Invoices',
      desc: 'Invoice created'
    }, {
      type: 'updated',
      section: 'Invoices',
      desc: 'Invoice updated'
    }, {
      type: 'approved',
      section: 'Bills',
      desc: 'Bill approved'
    }, {
      type: 'created',
      section: 'Refunds',
      desc: 'Refund created'
    }, {
      type: 'updated',
      section: 'Settings',
      desc: 'Settings updated'
    }, {
      type: 'created',
      section: 'Users',
      desc: 'User invited'
    }, {
      type: 'updated',
      section: 'Security',
      desc: 'Security settings updated'
    }];
    const realUsers = [{
      id: '1',
      name: 'Ahmed Lee',
      email: 'ahmed@bilabilamart.my',
      role: 'Owner'
    }, {
      id: '2',
      name: 'Aishath Tan',
      email: 'aishath@bilabilamart.my',
      role: 'Finance'
    }, {
      id: '3',
      name: 'Ibrahim Rahman',
      email: 'ibrahim@bilabilamart.my',
      role: 'Operations'
    }];
    for (let i = 0; i < 80; i++) {
      const action = actions[Math.floor(Math.random() * actions.length)];
      const user = realUsers[Math.floor(Math.random() * realUsers.length)];
      const minutesAgo = Math.floor(Math.random() * 43200); // 30 days in minutes
      const timestamp = new Date(now.getTime() - minutesAgo * 60000);
      logs.push({
        id: `log-${i + 1}`,
        timestamp: timestamp.toISOString(),
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role
        },
        action: action.type,
        section: action.section,
        description: action.desc,
        objectId: `${action.section.toLowerCase()}_${Math.random().toString(36).substr(2, 9)}`,
        details: {
          amount: Math.random() > 0.5 ? `MYR ${(Math.random() * 1000).toFixed(2)}` : undefined
        },
        ipAddress: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.3.'
      });
    }
    return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  });
  const [logFilters, setLogFilters] = useState({
    search: '',
    dateRange: '7days',
    user: 'all',
    actionType: 'all',
    section: 'all'
  });
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [currentLogPage, setCurrentLogPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  // Merchant Requests state
  const [merchantRequests] = useState(() => {
    const now = new Date();
    return [{
      id: 'REQ-001',
      type: 'Payment Method Activation',
      description: 'Request to activate ShopeePay payment method',
      requestedBy: 'Ahmed Lee',
      requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        paymentMethod: 'ShopeePay',
        reason: 'Customer demand for ShopeePay payments'
      }
    }, {
      id: 'REQ-002',
      type: 'User Role Request',
      description: 'Request for custom "Customer Support" role',
      requestedBy: 'Aishath Tan',
      requestedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        roleName: 'Customer Support',
        permissions: 'Read-only access to payments and customers'
      }
    }, {
      id: 'REQ-003',
      type: 'Payment Method Activation',
      description: 'Request to activate Boost eWallet',
      requestedBy: 'Ibrahim Rahman',
      requestedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'Boost',
        reason: 'Expand payment options for customers',
        rejectionReason: 'Merchant category not supported by Boost'
      }
    }, {
      id: 'REQ-004',
      type: 'Payment Method Activation',
      description: 'Request to activate American Express',
      requestedBy: 'Ahmed Lee',
      requestedAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'In Progress',
      details: {
        paymentMethod: 'American Express',
        reason: 'Corporate clients prefer Amex'
      }
    }, {
      id: 'REQ-005',
      type: 'User Role Request',
      description: 'Request for "Marketing" role with limited access',
      requestedBy: 'Aishath Tan',
      requestedAt: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        roleName: 'Marketing',
        permissions: 'Read access to reports and customer data'
      }
    }, {
      id: 'REQ-006',
      type: 'Payment Method Activation',
      description: 'Request to activate DuitNow QR',
      requestedBy: 'Ibrahim Rahman',
      requestedAt: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'DuitNow QR',
        reason: 'Low-cost payment option for customers'
      }
    }].sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
  });
  const tabs: TabConfig[] = [
  // ACCOUNT
  {
    id: 'company',
    label: 'Company',
    icon: BuildingIcon,
    category: 'account'
  }, {
    id: 'branding',
    label: 'Branding',
    icon: PaletteIcon,
    category: 'account'
  }, {
    id: 'users',
    label: 'Manage Users',
    icon: UsersIcon,
    category: 'account'
  }, {
    id: 'payout',
    label: 'Payout',
    icon: DollarSignIcon,
    category: 'account'
  },
  // PAYMENT SETTINGS
  {
    id: 'general',
    label: 'General',
    icon: SettingsIcon,
    category: 'payment'
  }, {
    id: 'payment-providers',
    label: 'Payment Providers',
    icon: CreditCardIcon,
    category: 'payment'
  }, {
    id: 'receipt',
    label: 'Receipt Information',
    icon: MailIcon,
    category: 'payment'
  }, {
    id: 'fields',
    label: 'Information Fields',
    icon: FormInputIcon,
    category: 'payment'
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: BellIcon,
    category: 'payment'
  }, {
    id: 'security',
    label: 'Security',
    icon: ShieldIcon,
    category: 'payment'
  },
  // E-COMMERCE
  {
    id: 'subscription-plan',
    label: 'Subscription Plan',
    icon: CreditCardIcon,
    category: 'account'
  }];
  const categories = [{
    id: 'account',
    label: 'ACCOUNT'
  }, {
    id: 'payment',
    label: 'PAYMENT SETTINGS'
  }];
  const SaveButton = () => <div className="pt-4" data-id="element-10136">
      <button className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md hover:bg-[#EB4A42] transition-colors" data-id="element-10137">
        Save Changes
      </button>
    </div>;
  // ---- Render functions ----
  const renderCompanySettings = () => <div className="space-y-6" data-id="element-10138">
      <div data-id="element-10139">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10140">
          Company Information
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10141">
          Manage your business details and merchant account information
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10142">
        <div className="flex items-start gap-3" data-id="element-10143">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10144" />
          <div className="text-sm text-gray-700" data-id="element-10145">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10146">
              Verified Merchant Account
            </p>
            <p className="text-xs" data-id="element-10147">
              Your merchant account has been verified and is active. Contact
              support if you need to update your business registration details.
            </p>
          </div>
        </div>
      </div>

      {/* Company Details Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10148">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10149">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10150">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-10151" />
            Business Details
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10152">
          <FormField label="Merchant ID" tooltip="Your unique merchant identifier" data-id="element-10153">
            <div className="flex items-center gap-2" data-id="element-10154">
              <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50" value={companyInfo.merchantId} readOnly data-id="element-10155" />
              <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-green-100 text-green-800 whitespace-nowrap" data-id="element-10156">
                <CheckCircleIcon size={14} className="mr-1" data-id="element-10157" />
                VERIFIED
              </span>
            </div>
          </FormField>

          <FormField label="Business Name" required data-id="element-10158">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessName} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessName: e.target.value
          })} data-id="element-10159" />
          </FormField>

          <FormField label="Registration Number" required data-id="element-10160">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.registrationNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            registrationNumber: e.target.value
          })} data-id="element-10161" />
          </FormField>

          <FormField label={getLocaleProfile().taxIdLabel} data-id="element-10162">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent" value={companyInfo.gstNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            gstNumber: e.target.value
          })} data-id="element-10163" />
          </FormField>

          <FormField label="Business Email" required data-id="element-10164">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessEmail} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessEmail: e.target.value
          })} data-id="element-10165" />
          </FormField>

          <FormField label="Business Phone" required data-id="element-10166">
            <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessPhone} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessPhone: e.target.value
          })} data-id="element-10167" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10168" />
    </div>;
  const renderBrandingSettings = () => <div className="space-y-6" data-id="element-10169">
      <div data-id="element-10170">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10171">Branding</h2>
        <p className="text-sm text-gray-600" data-id="element-10172">
          Customize your brand appearance across payment pages and receipts
        </p>
      </div>

      {/* Logo Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10173">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10174">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10175">
            <PaletteIcon size={18} className="text-gray-600" data-id="element-10176" />
            Logo & Visual Identity
          </h3>
        </div>
        <div className="p-6 space-y-6" data-id="element-10177">
          <FormField label="Company Logo" description="Upload your company logo (recommended: 200x60px, PNG or SVG)." data-id="element-10178">
            <div className="flex items-center gap-4" data-id="element-10179">
              {brandingSettings.logoUrl && <div className="border border-gray-200 rounded-lg p-4 bg-white" data-id="element-10180">
                  <img src={brandingSettings.logoUrl} alt="Company Logo" className="h-12 object-contain" data-id="element-10181" />
                </div>}
              <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center transition-colors" data-id="element-10182">
                <UploadIcon size={16} className="mr-2" data-id="element-10183" />
                Upload New Logo
              </button>
            </div>
          </FormField>

          <FormField label="Primary Brand Color" description="This color will be used for buttons and accents" data-id="element-10184">
            <div className="flex items-center gap-3" data-id="element-10185">
              <input type="color" className="h-10 w-20 border border-gray-300 rounded cursor-pointer" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-10186" />
              <input type="text" className="block w-32 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-10187" />
              <div className="flex-1 text-sm text-gray-600" data-id="element-10188">
                Current: BML Red
              </div>
            </div>
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10189" />
    </div>;
  const renderGeneralSettings = () => <div className="space-y-6" data-id="element-10190">
      <div data-id="element-10191">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10192">
          General Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10193">
          Configure default behaviors and preferences for your payment system
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10194">
        <div className="flex items-start gap-3" data-id="element-10195">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10196" />
          <div className="text-sm text-gray-700" data-id="element-10197">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10198">
              Payment Link Defaults
            </p>
            <p className="text-xs" data-id="element-10199">
              These settings apply to all new payment links and invoices. You
              can override them individually when creating each payment.
            </p>
          </div>
        </div>
      </div>

      {/* Settings Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10200">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10201">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10202">
            <SettingsIcon size={18} className="text-gray-600" data-id="element-10203" />
            Default Configuration
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10204">
          <FormField label="Default Expiry Days" description="Number of days before payment links expire" data-id="element-10205">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={generalSettings.defaultExpiryDays} onChange={e => setGeneralSettings({
            ...generalSettings,
            defaultExpiryDays: parseInt(e.target.value)
          })} data-id="element-10206" />
          </FormField>

          <FormField label="Redirect URL" description="Where customers are sent after successful payment" data-id="element-10207">
            <input type="url" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-mono text-sm" value={generalSettings.redirectUrl} onChange={e => setGeneralSettings({
            ...generalSettings,
            redirectUrl: e.target.value
          })} data-id="element-10208" />
          </FormField>

          <div className="pt-2" data-id="element-10209">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-10210">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[#E2231A] focus:ring-[#E2231A]" checked={generalSettings.enableAutoReminders} onChange={e => setGeneralSettings({
              ...generalSettings,
              enableAutoReminders: e.target.checked
            })} data-id="element-10211" />
              <div data-id="element-10212">
                <p className="text-sm font-medium text-gray-900" data-id="element-10213">
                  Enable automatic payment reminders
                </p>
                <p className="text-xs text-gray-500" data-id="element-10214">
                  Send reminder emails for unpaid invoices
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      <SaveButton data-id="element-10215" />
    </div>;
  const renderReceiptSettings = () => <div className="space-y-6" data-id="element-10216">
      <div data-id="element-10217">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10218">
          Receipt Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10219">
          Customize the receipts sent to your customers after payment
        </p>
      </div>

      {/* Receipt Customization Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10220">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10221">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10222">
            <MailIcon size={18} className="text-gray-600" data-id="element-10223" />
            Receipt Customization
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10224">
          <div className="pb-2" data-id="element-10225">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-10226">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[#E2231A] focus:ring-[#E2231A]" checked={receiptSettings.showCompanyAddress} onChange={e => setReceiptSettings({
              ...receiptSettings,
              showCompanyAddress: e.target.checked
            })} data-id="element-10227" />
              <div data-id="element-10228">
                <p className="text-sm font-medium text-gray-900" data-id="element-10229">
                  Show company address on receipts
                </p>
                <p className="text-xs text-gray-500" data-id="element-10230">
                  Display your business address on customer receipts
                </p>
              </div>
            </label>
          </div>

          <FormField label="Company Address" description="This address will appear on receipts if enabled above" data-id="element-10231">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" rows={3} value={receiptSettings.companyAddress} onChange={e => setReceiptSettings({
            ...receiptSettings,
            companyAddress: e.target.value
          })} data-id="element-10232" />
          </FormField>

          <FormField label="CC Email" description="Automatically CC this email on all receipts" data-id="element-10233">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={receiptSettings.ccEmail} onChange={e => setReceiptSettings({
            ...receiptSettings,
            ccEmail: e.target.value
          })} data-id="element-10234" />
          </FormField>

          <FormField label="Footer Text" description="Custom message at the bottom of receipts" data-id="element-10235">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={receiptSettings.footerText} onChange={e => setReceiptSettings({
            ...receiptSettings,
            footerText: e.target.value
          })} data-id="element-10236" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10237" />
    </div>;
  const renderPaymentProviders = () => <div className="space-y-6" data-id="element-10238">
      <div data-id="element-10239">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10240">
          Payment Providers
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10241">
          Enable or disable payment methods available to your customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-10242">
        <div className="flex items-start gap-3" data-id="element-10243">
          <AlertCircleIcon size={20} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-10244" />
          <div className="text-sm text-gray-700" data-id="element-10245">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10246">
              Payment Method Availability
            </p>
            <p className="text-xs" data-id="element-10247">
              Disabling a payment method will hide it from all payment pages.
              Customers won't be able to use disabled methods for new payments.
            </p>
          </div>
        </div>
      </div>

      {/* Card Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10248">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10249">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10250">
            <CreditCardIcon size={18} className="text-gray-600" data-id="element-10251" />
            Card Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10252">
          {paymentMethods.filter(m => m.category === 'card').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10253">
                  <div className="flex items-center gap-3" data-id="element-10254">
                    <Icon size={20} className="text-gray-600" data-id="element-10255" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-10256">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-10257">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-10258" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10259"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* E-Wallet Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10260">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10261">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10262">
            <WalletIcon size={18} className="text-gray-600" data-id="element-10263" />
            E-Wallets & QR Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10264">
          {paymentMethods.filter(m => m.category === 'ewallet').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10265">
                  <div className="flex items-center gap-3" data-id="element-10266">
                    <Icon size={20} className="text-gray-600" data-id="element-10267" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-10268">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-10269">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-10270" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10271"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* Online Banking */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10272">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10273">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10274">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-10275" />
            Online Banking
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10276">
          {paymentMethods.filter(m => m.category === 'banking').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10277">
                  <div className="flex items-center gap-3" data-id="element-10278">
                    <Icon size={20} className="text-gray-600" data-id="element-10279" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-10280">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-10281">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-10282" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10283"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>
    </div>;
  const renderUsers = () => <div className="space-y-6" data-id="element-10284">
      <div className="flex items-center justify-between" data-id="element-10285">
        <div data-id="element-10286">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10287">
            Users & Permissions
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10288">
            Manage team members and their access levels
          </p>
        </div>
        <div className="flex gap-3" data-id="element-10289">
          <button onClick={() => setShowRoleManagementModal(true)} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md flex items-center hover:bg-gray-50 transition-colors" data-id="element-10290">
            <ShieldIcon size={16} className="mr-2" data-id="element-10291" />
            Permissions
          </button>
          <button onClick={() => setShowInviteForm(!showInviteForm)} className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md flex items-center hover:bg-[#EB4A42] transition-colors" data-id="element-10292">
            <PlusIcon size={16} className="mr-2" data-id="element-10293" />
            Invite User
          </button>
        </div>
      </div>

      {/* Invite Form */}
      {showInviteForm && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10294">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10295">
            <h3 className="text-md font-semibold text-gray-900" data-id="element-10296">
              Invite New User
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-10297">
            <FormField label="Email Address" required data-id="element-10298">
              <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="user@example.com" data-id="element-10299" />
            </FormField>
            <FormField label="Role" required data-id="element-10300">
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={inviteRole} onChange={e => setInviteRole(e.target.value)} data-id="element-10301">
                <option value="Owner" data-id="element-10302">Owner</option>
                <option value="Merchant Management" data-id="element-10303">Merchant Management</option>
                <option value="Account Management" data-id="element-10304">Account Management</option>
                <option value="Sales" data-id="element-10305">Sales</option>
                <option value="Finance" data-id="element-10306">Finance</option>
                <option value="Operations" data-id="element-10307">Operations</option>
              </select>
              {inviteRole && rolePermissions[inviteRole as keyof typeof rolePermissions] && <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md" data-id="element-10308">
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-10309">
                      {inviteRole}
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-10310">
                      {rolePermissions[inviteRole as keyof typeof rolePermissions].description}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-10311">
                      {Object.entries(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).slice(0, showAllInvitePerms ? Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length : 5).map(([section, perms]) => {
                const activePerms = Object.entries(perms).filter(([_, val]) => val).map(([key]) => key.charAt(0).toUpperCase() + key.slice(1));
                if (activePerms.length === 0) return null;
                return <span key={section} className="text-xs px-2 py-1 bg-white border border-blue-200 rounded text-gray-700" data-id="element-10312">
                              {section}: {activePerms.join(', ')}
                            </span>;
              })}
                      {Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length > 5 && <button type="button" onClick={() => setShowAllInvitePerms(!showAllInvitePerms)} className="text-xs px-2 py-1 text-blue-600 hover:text-blue-700 hover:underline font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded" data-id="element-10313">
                          {showAllInvitePerms ? 'Show less' : `+${Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length - 5} more`}
                        </button>}
                    </div>
                  </div>}
            </FormField>
            <div className="flex gap-3 pt-2" data-id="element-10314">
              <button onClick={() => {
            if (inviteEmail && inviteRole) {
              const newUser = {
                id: `user-${Date.now()}`,
                name: inviteEmail.split('@')[0],
                email: inviteEmail,
                role: inviteRole,
                status: 'Invited' as const
              };
              setUsers([...users, newUser]);
              setInviteEmail('');
              setInviteRole('Manager');
              setShowInviteForm(false);
            }
          }} className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md hover:bg-[#EB4A42] transition-colors" data-id="element-10315">
                Send Invite
              </button>
              <button onClick={() => {
            setShowInviteForm(false);
            setInviteEmail('');
            setInviteRole('Operations');
          }} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors" data-id="element-10316">
                Cancel
              </button>
            </div>
          </div>
        </div>}

      {/* Users Table */}
      <div className="border border-gray-200 rounded-lg" data-id="element-10317">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg" data-id="element-10318">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10319">
            <UsersIcon size={18} className="text-gray-600" data-id="element-10320" />
            Team Members ({users.length})
          </h3>
        </div>
        <div data-id="element-10321">
          <table className="w-full" data-id="element-10322">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10323">
              <tr data-id="element-10324">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10325">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10326">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10327">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10328">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10329">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10330">
              {users.map(u => <tr key={u.id} className="hover:bg-gray-50 transition-colors" data-id="element-10331">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-10332">
                    {u.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-10333">{u.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-10334">{u.role}</td>
                  <td className="px-6 py-4" data-id="element-10335">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${u.status === 'Active' ? 'bg-green-100 text-green-800' : u.status === 'Invited' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10336">
                      {u.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm" data-id="element-10337">
                    <div className="flex items-center justify-end gap-2" data-id="element-10338">
                      {u.role === 'Owner' ? <span className="text-gray-400 text-xs" data-id="element-10339">Owner</span> : <div className="relative inline-block" data-id="element-10340">
                          <button type="button" aria-label={`Actions for ${u.name}`} aria-haspopup="menu" aria-expanded={openUserMenuId === u.id} onClick={() => setOpenUserMenuId(openUserMenuId === u.id ? null : u.id)} className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-10341">
                            <MoreHorizontalIcon size={18} data-id="element-10342" />
                          </button>
                          {openUserMenuId === u.id && <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenUserMenuId(null)} data-id="element-10343" />
                              <div role="menu" className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-20" data-id="element-10344">
                                {u.status !== 'Invited' && <button role="menuitem" onClick={() => {
                          setEditingUser({
                            id: u.id,
                            name: u.name,
                            email: u.email,
                            role: u.role
                          });
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-10345">
                                    Edit
                                  </button>}
                                {u.status === 'Active' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Disabled' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-amber-700 hover:bg-amber-50" data-id="element-10346">
                                    Disable
                                  </button>}
                                {u.status === 'Disabled' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Active' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50" data-id="element-10347">
                                    Enable
                                  </button>}
                                <button role="menuitem" onClick={() => {
                          if (confirm(u.status === 'Invited' ? `Cancel invite for ${u.email}?` : `Are you sure you want to delete ${u.name}?`)) {
                            setUsers(users.filter(user => user.id !== u.id));
                          }
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-10348">
                                  {u.status === 'Invited' ? 'Cancel Invite' : 'Delete'}
                                </button>
                              </div>
                            </>}
                        </div>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-10349">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" data-id="element-10350">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-10351">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-10352">
                Edit Team Member
              </h3>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-10353">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-10354">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-10355" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-10356">
              <FormField label="Name" data-id="element-10357">
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.name} onChange={e => setEditingUser({
              ...editingUser,
              name: e.target.value
            })} data-id="element-10358" />
              </FormField>
              <FormField label="Email" data-id="element-10359">
                <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.email} onChange={e => setEditingUser({
              ...editingUser,
              email: e.target.value
            })} data-id="element-10360" />
              </FormField>
              <FormField label="Role" data-id="element-10361">
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.role} onChange={e => setEditingUser({
              ...editingUser,
              role: e.target.value
            })} data-id="element-10362">
                  <option value="Merchant Management" data-id="element-10363">
                    Merchant Management
                  </option>
                  <option value="Account Management" data-id="element-10364">Account Management</option>
                  <option value="Sales" data-id="element-10365">Sales</option>
                  <option value="Finance" data-id="element-10366">Finance</option>
                  <option value="Operations" data-id="element-10367">Operations</option>
                </select>
              </FormField>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50" data-id="element-10368">
              <button onClick={() => setEditingUser(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-10369">
                Cancel
              </button>
              <button onClick={() => {
            setUsers(users.map(user => user.id === editingUser.id ? {
              ...user,
              name: editingUser.name,
              email: editingUser.email,
              role: editingUser.role
            } : user));
            setEditingUser(null);
          }} className="px-4 py-2 text-sm font-medium bg-[#E2231A] text-white rounded-md hover:bg-[#EB4A42]" data-id="element-10370">
                Save Changes
              </button>
            </div>
          </div>
        </div>}

      {/* Role Management Modal */}
      {showRoleManagementModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-10371">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-10372">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-10373">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-10374">
                Role Permissions Matrix
              </h3>
              <button onClick={() => setShowRoleManagementModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-10375">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-10376">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-10377" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto flex-1" data-id="element-10378">
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-10379">
                {Object.entries(rolePermissions).map(([role, config]) => <div key={role} className="border border-gray-200 rounded-lg p-4" data-id="element-10380">
                    <h4 className="font-semibold text-gray-900 mb-1" data-id="element-10381">{role}</h4>
                    <p className="text-xs text-gray-600" data-id="element-10382">
                      {config.description}
                    </p>
                  </div>)}
              </div>
              <div className="overflow-x-auto" data-id="element-10383">
                <table className="w-full border border-gray-200 rounded-lg" data-id="element-10384">
                  <thead className="bg-gray-50" data-id="element-10385">
                    <tr data-id="element-10386">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-10387">
                        Section
                      </th>
                      {Object.keys(rolePermissions).map(role => <th key={role} colSpan={5} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-l border-gray-200" data-id="element-10388">
                          {role}
                        </th>)}
                    </tr>
                    <tr data-id="element-10389">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10390"></th>
                      {Object.keys(rolePermissions).map(role => <Fragment key={role} data-id="element-10391">
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-l border-gray-200" data-id="element-10392">
                            R
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10393">
                            W
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10394">
                            M
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10395">
                            I
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10396">
                            A
                          </th>
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-10397">
                    {Object.keys(rolePermissions.Owner.permissions).map(section => <tr key={section} className="hover:bg-gray-50" data-id="element-10398">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10399">
                            {section}
                          </td>
                          {Object.entries(rolePermissions).map(([role, config]) => {
                    const perms = config.permissions[section as keyof typeof config.permissions];
                    return <Fragment key={role} data-id="element-10400">
                                  <td className="px-2 py-3 text-center border-l border-gray-200" data-id="element-10401">
                                    {perms.read ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10402" /> : <span className="text-gray-300" data-id="element-10403">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10404">
                                    {perms.write ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10405" /> : <span className="text-gray-300" data-id="element-10406">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10407">
                                    {perms.modify ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10408" /> : <span className="text-gray-300" data-id="element-10409">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10410">
                                    {perms.insert ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10411" /> : <span className="text-gray-300" data-id="element-10412">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10413">
                                    {perms.approve ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10414" /> : <span className="text-gray-300" data-id="element-10415">—</span>}
                                  </td>
                                </Fragment>;
                  })}
                        </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-xs text-gray-500" data-id="element-10416">
                <p className="font-medium mb-1" data-id="element-10417">Permission Legend:</p>
                <div className="flex flex-wrap gap-4" data-id="element-10418">
                  <span data-id="element-10419">
                    <strong data-id="element-10420">R</strong> = Read
                  </span>
                  <span data-id="element-10421">
                    <strong data-id="element-10422">W</strong> = Write
                  </span>
                  <span data-id="element-10423">
                    <strong data-id="element-10424">M</strong> = Modify
                  </span>
                  <span data-id="element-10425">
                    <strong data-id="element-10426">I</strong> = Insert
                  </span>
                  <span data-id="element-10427">
                    <strong data-id="element-10428">A</strong> = Approve
                  </span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-10429">
              <button onClick={() => setShowRoleManagementModal(false)} className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md hover:bg-[#EB4A42] transition-colors" data-id="element-10430">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
  const renderTerms = () => <div className="space-y-6" data-id="element-10431">
      <div data-id="element-10432">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10433">
          Terms & Privacy
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10434">
          Configure legal terms and privacy policy shown to customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10435">
        <div className="flex items-start gap-3" data-id="element-10436">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10437" />
          <div className="text-sm text-gray-700" data-id="element-10438">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10439">Legal Compliance</p>
            <p className="text-xs" data-id="element-10440">
              These terms are displayed to customers during checkout. Ensure
              they comply with Malaysian data protection and consumer laws.
            </p>
          </div>
        </div>
      </div>

      {/* Terms Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10441">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10442">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10443">
            <FileTextIcon size={18} className="text-gray-600" data-id="element-10444" />
            Terms & Conditions
          </h3>
        </div>
        <div className="p-6" data-id="element-10445">
          <FormField label="Terms Text" description="Displayed on payment pages and checkout" data-id="element-10446">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-sans" rows={6} value={termsSettings.termsText} onChange={e => setTermsSettings({
            ...termsSettings,
            termsText: e.target.value
          })} data-id="element-10447" />
          </FormField>
        </div>
      </div>

      {/* Privacy Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10448">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10449">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10450">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-10451" />
            Privacy Policy
          </h3>
        </div>
        <div className="p-6" data-id="element-10452">
          <FormField label="Privacy Policy Text" description="Information about how customer data is handled" data-id="element-10453">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-sans" rows={6} value={termsSettings.privacyPolicy} onChange={e => setTermsSettings({
            ...termsSettings,
            privacyPolicy: e.target.value
          })} data-id="element-10454" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10455" />
    </div>;
  const renderFields = () => <div className="space-y-6" data-id="element-10456">
      <div data-id="element-10457">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10458">
          Information Fields
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10459">
          Configure metadata fields collected at checkout
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10460">
        <div className="flex items-start gap-3" data-id="element-10461">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10462" />
          <div className="text-sm text-gray-700" data-id="element-10463">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10464">
              Custom Checkout Fields
            </p>
            <p className="text-xs" data-id="element-10465">
              Add custom fields to collect additional information from customers
              during checkout. This data will be attached to payment records and
              can be used for reporting or integration purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Fields Editor Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10466">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10467">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10468">
            <FormInputIcon size={18} className="text-gray-600" data-id="element-10469" />
            Metadata Fields
          </h3>
        </div>
        <div className="p-6" data-id="element-10470">
          <MetadataEditor metadata={presetMetadataFields} onChange={setPresetMetadataFields} data-id="element-10471" />
        </div>
      </div>
    </div>;
  const renderNotifications = () => <div className="space-y-6" data-id="element-10472">
      <div data-id="element-10473">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10474">
          Notifications
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10475">
          Manage how you receive alerts about payments and account activity
        </p>
      </div>

      {/* Notification Channels Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10476">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10477">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10478">
            <BellIcon size={18} className="text-gray-600" data-id="element-10479" />
            Notification Channels
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10480">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10481">
            <div data-id="element-10482">
              <p className="text-sm font-medium text-gray-900" data-id="element-10483">
                Email Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10484">
                Receive alerts via email
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10485">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.emailNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              emailNotifications: e.target.checked
            })} data-id="element-10486" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10487"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10488">
            <div data-id="element-10489">
              <p className="text-sm font-medium text-gray-900" data-id="element-10490">
                SMS Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10491">
                Receive alerts via text message
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10492">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.smsNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              smsNotifications: e.target.checked
            })} data-id="element-10493" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10494"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Event Notifications Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10495">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10496">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10497">
            <AlertCircleIcon size={18} className="text-gray-600" data-id="element-10498" />
            Event Notifications
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10499">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10500">
            <div data-id="element-10501">
              <p className="text-sm font-medium text-gray-900" data-id="element-10502">
                Payment Received
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10503">
                When a customer completes payment
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10504">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentReceived} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentReceived: e.target.checked
            })} data-id="element-10505" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10506"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10507">
            <div data-id="element-10508">
              <p className="text-sm font-medium text-gray-900" data-id="element-10509">
                Payment Failed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10510">
                When a payment attempt fails
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10511">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentFailed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentFailed: e.target.checked
            })} data-id="element-10512" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10513"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10514">
            <div data-id="element-10515">
              <p className="text-sm font-medium text-gray-900" data-id="element-10516">
                Refund Processed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10517">
                When a refund is completed
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10518">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.refundProcessed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              refundProcessed: e.target.checked
            })} data-id="element-10519" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10520"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10521">
            <div data-id="element-10522">
              <p className="text-sm font-medium text-gray-900" data-id="element-10523">
                Dispute Opened
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10524">
                When a customer files a dispute
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10525">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.disputeOpened} onChange={e => setNotificationSettings({
              ...notificationSettings,
              disputeOpened: e.target.checked
            })} data-id="element-10526" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10527"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10528">
            <div data-id="element-10529">
              <p className="text-sm font-medium text-gray-900" data-id="element-10530">
                Settlement Completed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10531">
                When funds are transferred to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10532">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.settlementCompleted} onChange={e => setNotificationSettings({
              ...notificationSettings,
              settlementCompleted: e.target.checked
            })} data-id="element-10533" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10534"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10535">
            <div data-id="element-10536">
              <p className="text-sm font-medium text-gray-900" data-id="element-10537">Low Balance</p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10538">
                When your account balance is low
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10539">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.lowBalance} onChange={e => setNotificationSettings({
              ...notificationSettings,
              lowBalance: e.target.checked
            })} data-id="element-10540" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10541"></div>
            </label>
          </div>
        </div>
      </div>
    </div>;
  const renderSecurity = () => <div className="space-y-6" data-id="element-10542">
      <div data-id="element-10543">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10544">Security</h2>
        <p className="text-sm text-gray-600" data-id="element-10545">
          Manage security settings and access controls for your account
        </p>
      </div>

      {/* Security Status Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-10546">
        <div className="flex items-start gap-3" data-id="element-10547">
          <CheckCircleIcon size={20} className="text-green-600 flex-shrink-0 mt-0.5" data-id="element-10548" />
          <div className="text-sm text-gray-700" data-id="element-10549">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10550">
              Security Status: Protected
            </p>
            <p className="text-xs" data-id="element-10551">
              Your account is secured with two-factor authentication and session
              management. Keep these settings enabled for maximum protection.
            </p>
          </div>
        </div>
      </div>

      {/* Authentication Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10552">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10553">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10554">
            <LockIcon size={18} className="text-gray-600" data-id="element-10555" />
            Authentication & Access
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10556">
          <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-4" data-id="element-10557">
            <div data-id="element-10558">
              <p className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-10559">
                Two-Factor Authentication
                {securitySettings.twoFactorEnabled && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10560">
                    Enabled
                  </span>}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10561">
                Adds an extra layer of security to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10562">
              <input type="checkbox" className="sr-only peer" checked={securitySettings.twoFactorEnabled} onChange={e => setSecuritySettings({
              ...securitySettings,
              twoFactorEnabled: e.target.checked
            })} data-id="element-10563" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10564"></div>
            </label>
          </div>

          <FormField label="Session Timeout (minutes)" description="Automatically log out after this period of inactivity" data-id="element-10565">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={securitySettings.sessionTimeout} onChange={e => setSecuritySettings({
            ...securitySettings,
            sessionTimeout: parseInt(e.target.value)
          })} data-id="element-10566" />
          </FormField>
        </div>
      </div>

      {/* IP Whitelist Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10567">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10568">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10569">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-10570" />
            IP Whitelist
          </h3>
        </div>
        <div className="p-6" data-id="element-10571">
          <FormField label="Allowed IP Addresses" description="Restrict API access to specific IP addresses (one per line). Leave empty to allow all IPs." data-id="element-10572">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" rows={4} value={securitySettings.ipWhitelist} onChange={e => setSecuritySettings({
            ...securitySettings,
            ipWhitelist: e.target.value
          })} placeholder="192.168.1.1&#10;10.0.0.1" data-id="element-10573" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10574" />
    </div>;
  const renderPayoutSettings = () => {
    const isMinBalanceInvalid = minBalanceEnabled && (!minBalanceAmount || Number(minBalanceAmount) <= 0);
    let pendingPayoutUpdate: {
      bank?: string;
      maskedAccount?: string;
      submittedAt?: string;
    } | null = null;
    try {
      const raw = typeof window !== 'undefined' ? sessionStorage.getItem('pendingPayoutUpdate') : null;
      if (raw) pendingPayoutUpdate = JSON.parse(raw);
    } catch {
      pendingPayoutUpdate = null;
    }
    return <div className="space-y-6" data-id="element-10575">
        <div data-id="element-10576">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10577">
            Payout Settings
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10578">
            Configure bank accounts, minimum balance, and payout preferences
          </p>
        </div>

        {/* Settlement Currencies Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10579">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10580">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10581">
              <BuildingIcon size={18} className="text-gray-600" data-id="element-10582" />
              Settlement Currencies & Bank Accounts
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-10583">
              Configure bank accounts for receiving payouts in different
              currencies
            </p>
          </div>

          <div className="overflow-x-auto" data-id="element-10584">
            <table className="w-full text-left" data-id="element-10585">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10586">
                <tr data-id="element-10587">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10588">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10589">
                    Payout Bank Account
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10590">
                    <div className="flex items-center gap-1.5" data-id="element-10591">
                      Payout Fee
                      <InfoIcon size={14} className="text-gray-400" data-id="element-10592" />
                    </div>
                  </th>
                  <th className="px-6 py-3" data-id="element-10593"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10594">
                <tr className="hover:bg-gray-50 transition-colors" data-id="element-10595">
                  <td className="px-6 py-4" data-id="element-10596">
                    <div className="flex items-center gap-2" data-id="element-10597">
                      <span className="text-sm font-medium text-gray-900" data-id="element-10598">
                        MYR
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10599">
                        Default
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-10600">
                    <div className="flex flex-col gap-1.5" data-id="element-10601">
                      <div className="flex items-center gap-3" data-id="element-10602">
                        <div className="w-8 h-8 rounded bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-10603">
                          <BuildingIcon size={16} className="text-gray-500" data-id="element-10604" />
                        </div>
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-10605">
                          <span className="text-sm text-gray-900" data-id="element-10606">
                            {BRAND.name}
                          </span>
                          <span className="text-sm text-gray-500 font-mono" data-id="element-10607">
                            ••••5678
                          </span>
                        </div>
                      </div>
                      {pendingPayoutUpdate && <div className="ml-11 flex items-center gap-2 flex-wrap" data-id="element-10608">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200" data-id="element-10609">
                            <ClockIcon size={12} data-id="element-10610" />
                            Pending Bank Review
                          </span>
                          <span className="text-xs text-gray-600" data-id="element-10611">
                            New: {pendingPayoutUpdate.bank}{' '}
                            <span className="font-mono" data-id="element-10612">
                              {pendingPayoutUpdate.maskedAccount}
                            </span>
                          </span>
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-10613">
                    <span className="text-sm text-gray-600" data-id="element-10614">Free</span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-10615">
                    <div className="flex items-center justify-end gap-2" data-id="element-10616">
                      <button onClick={() => navigate('/settings/payout-account/edit')} disabled={!!pendingPayoutUpdate} title={pendingPayoutUpdate ? 'An update is pending bank review' : 'Edit payout account'} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10617">
                        Edit
                      </button>
                      <button aria-label="More options" className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-10618">
                        <MoreHorizontalIcon size={18} data-id="element-10619" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-10620">
            <button className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-10621">
              + Add a settlement currency
            </button>
          </div>
        </div>

        {/* Minimum Balance Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10622">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10623">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10624">
              <DollarSignIcon size={18} className="text-gray-600" data-id="element-10625" />
              Minimum Balance
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-10626">
              Keep a minimum amount in your account to cover refunds and
              chargebacks
            </p>
          </div>

          <div className="p-6" data-id="element-10627">
            <div className="flex items-start gap-4" data-id="element-10628">
              <label className="relative inline-flex items-center cursor-pointer mt-0.5" data-id="element-10629">
                <input type="checkbox" className="sr-only peer" checked={minBalanceEnabled} onChange={e => setMinBalanceEnabled(e.target.checked)} data-id="element-10630" />
                <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10631"></div>
              </label>

              <div className="flex-1" data-id="element-10632">
                <p className="text-sm font-medium text-gray-900" data-id="element-10633">
                  Keep a minimum amount in your account
                </p>
                <p className="text-sm text-gray-500 mt-0.5 mb-4" data-id="element-10634">
                  We'll adjust your payouts so you always maintain this balance
                </p>

                {minBalanceEnabled && <div className="mb-4" data-id="element-10635">
                    <div className="relative max-w-xs" data-id="element-10636">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10637">
                        <span className="text-gray-500 text-sm" data-id="element-10638">MYR</span>
                      </div>
                      <input type="number" value={minBalanceAmount} onChange={e => setMinBalanceAmount(e.target.value)} className={`block w-full pl-14 pr-3 py-2 text-sm rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent ${isMinBalanceInvalid ? 'border-red-300 text-red-900' : 'border-gray-300'}`} placeholder="0.00" data-id="element-10639" />
                    </div>
                    {isMinBalanceInvalid && <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5" data-id="element-10640">
                        <InfoIcon size={14} data-id="element-10641" />
                        Amount must be greater than 0
                      </p>}
                  </div>}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-10642">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-10643">
              Cancel
            </button>
            <button disabled={isMinBalanceInvalid} className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${isMinBalanceInvalid ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#E2231A] hover:bg-[#EB4A42] text-white'}`} data-id="element-10644">
              Save
            </button>
          </div>
        </div>

        {/* Statement Descriptor Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10645">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10646">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10647">
              <FileTextIcon size={18} className="text-gray-600" data-id="element-10648" />
              Payout Statement Descriptor
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-10649">
              Customize how payouts appear on your bank statement
            </p>
          </div>

          <div className="p-6 space-y-6" data-id="element-10650">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-10651">
              <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10652" />
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-10653">
                Set a custom statement descriptor to help identify payouts in
                your bank account. This will appear on your bank statement for
                all payouts.
              </p>
            </div>

            <FormField label="Statement Descriptor" description="This text will appear on your bank statement" data-id="element-10654">
              <input type="text" value={payoutDescriptor} onChange={e => setPayoutDescriptor(e.target.value)} placeholder={`Example - ${companyInfo.businessName}`} className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" data-id="element-10655" />
            </FormField>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-10656">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-10657">
              Cancel
            </button>
            <button className="px-4 py-2 text-sm font-medium bg-[#E2231A] hover:bg-[#EB4A42] text-white rounded-md transition-colors" data-id="element-10658">
              Save
            </button>
          </div>
        </div>
      </div>;
  };
  // MDR row component
  const MdrRow = ({
    type,
    tier,
    range,
    ic,
    sf,
    am,
    total,
    color
  }: any) => <tr className={color} data-id="element-10659">
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-10660">{type}</td>
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-10661">{tier}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10662">{range}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10663">{ic}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10664">{sf}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10665">{am}</td>
      <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-10666">{total}</td>
    </tr>;
  const FixedFee = ({
    label,
    sublabel,
    amount
  }: {
    label: string;
    sublabel: string;
    amount: number | string;
  }) => <div className="flex items-center justify-between" data-id="element-10667">
      <div data-id="element-10668">
        <p className="text-sm text-gray-900 font-medium" data-id="element-10669">{label}</p>
        <p className="text-xs text-gray-500" data-id="element-10670">{sublabel}</p>
      </div>
      <span className="text-sm font-semibold text-gray-900" data-id="element-10671">
        {typeof amount === 'number' ? formatCurrencyAmount(amount, {
        currency
      }) : amount}
      </span>
    </div>;
  const renderSubscriptionPlanSettings = () => <div data-id="element-10672">
      <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-10673">
        Subscription Plan
      </h2>
      <div className="space-y-6" data-id="element-10674">
        {/* Supported Payment Methods */}
        <div className="border-b border-gray-200 pb-6" data-id="element-10675">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-10676">
            <CreditCardIcon size={16} className="mr-2 text-blue-600" data-id="element-10677" />
            Supported Payment Methods
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-10678">
            Payment methods enabled for your account with their merchant
            discount rates
          </p>

          {/* Card Payments */}
          <div className="mb-6" data-id="element-10679">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-10680">
              Card Payments
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10681">
              {/* Visa */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10682">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-10683">
                  <div className="flex items-center justify-between" data-id="element-10684">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10685">
                      Visa
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10686">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-10687">
                  <div className="flex items-center justify-between" data-id="element-10688">
                    <span className="text-xs text-gray-600" data-id="element-10689">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10690">
                      0.80% - 1.30%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-10691">
                    <span className="text-xs text-gray-600" data-id="element-10692">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10693">
                      1.10% - 1.70%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10694">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-10695">
                      + MYR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-10696">
                      <p className="text-xs text-gray-600" data-id="element-10697">
                        <span className="font-medium" data-id="element-10698">MYR 1-1,000:</span> 0.80%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-10699">
                        <span className="font-medium" data-id="element-10700">MYR 1,000+:</span> 1.30%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mastercard */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10701">
                <div className="bg-orange-50 px-4 py-3 border-b border-orange-200" data-id="element-10702">
                  <div className="flex items-center justify-between" data-id="element-10703">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10704">
                      Mastercard
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10705">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-10706">
                  <div className="flex items-center justify-between" data-id="element-10707">
                    <span className="text-xs text-gray-600" data-id="element-10708">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10709">
                      0.85% - 1.35%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-10710">
                    <span className="text-xs text-gray-600" data-id="element-10711">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10712">
                      1.15% - 1.75%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10713">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-10714">
                      + MYR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-10715">
                      <p className="text-xs text-gray-600" data-id="element-10716">
                        <span className="font-medium" data-id="element-10717">MYR 1-500:</span> 0.85%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-10718">
                        <span className="font-medium" data-id="element-10719">MYR 500+:</span> 1.35%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* American Express */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10720">
                <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200" data-id="element-10721">
                  <div className="flex items-center justify-between" data-id="element-10722">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10723">
                      American Express
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10724">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-10725">
                  <div className="flex items-center justify-between" data-id="element-10726">
                    <span className="text-xs text-gray-600" data-id="element-10727">All Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10728">
                      2.40% - 2.60%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10729">
                    <p className="text-xs text-gray-500" data-id="element-10730">
                      + MYR 0.75 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* E-Wallets & Online Banking */}
          <div data-id="element-10731">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-10732">
              E-Wallets & Online Banking
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-10733">
              {/* Touch 'n Go */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10734">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-10735">
                  <div className="flex items-center justify-between" data-id="element-10736">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10737">
                      Touch 'n Go
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10738">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10739">
                  <div className="flex items-center justify-between mb-2" data-id="element-10740">
                    <span className="text-xs text-gray-600" data-id="element-10741">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10742">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10743">
                    <p className="text-xs text-gray-500" data-id="element-10744">
                      + MYR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* GrabPay */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10745">
                <div className="bg-green-50 px-4 py-3 border-b border-green-200" data-id="element-10746">
                  <div className="flex items-center justify-between" data-id="element-10747">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10748">
                      GrabPay
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10749">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10750">
                  <div className="flex items-center justify-between mb-2" data-id="element-10751">
                    <span className="text-xs text-gray-600" data-id="element-10752">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10753">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10754">
                    <p className="text-xs text-gray-500" data-id="element-10755">
                      + MYR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* FPX */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10756">
                <div className="bg-purple-50 px-4 py-3 border-b border-purple-200" data-id="element-10757">
                  <div className="flex items-center justify-between" data-id="element-10758">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10759">
                      FPX
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10760">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10761">
                  <div className="flex items-center justify-between mb-2" data-id="element-10762">
                    <span className="text-xs text-gray-600" data-id="element-10763">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10764">
                      1.20%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10765">
                    <p className="text-xs text-gray-500" data-id="element-10766">
                      + MYR 0.40 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* DuitNow QR */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10767">
                <div className="bg-amber-50 px-4 py-3 border-b border-amber-200" data-id="element-10768">
                  <div className="flex items-center justify-between" data-id="element-10769">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10770">
                      DuitNow QR
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10771">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10772">
                  <div className="flex items-center justify-between mb-2" data-id="element-10773">
                    <span className="text-xs text-gray-600" data-id="element-10774">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10775">
                      0.80%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10776">
                    <p className="text-xs text-gray-500" data-id="element-10777">
                      + MYR 0.25 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10778">
            <div className="flex items-start gap-2" data-id="element-10779">
              <InfoIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10780" />
              <div className="text-sm text-gray-700" data-id="element-10781">
                <p className="font-medium text-gray-900 mb-1" data-id="element-10782">Pricing Notes:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-10783">
                  <li data-id="element-10784">
                    Card rates vary based on card type (debit/credit) and
                    issuing bank
                  </li>
                  <li data-id="element-10785">
                    Lower rates apply to CIMB-issued cards (on-us transactions)
                  </li>
                  <li data-id="element-10786">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Fees & Additional Charges */}
        <div className="border-b border-gray-200 pb-6" data-id="element-10787">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-10788">
            <TagIcon size={16} className="mr-2 text-blue-600" data-id="element-10789" />
            Transaction Fees & Additional Charges
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-10790">
            Fixed fees and additional charges per transaction or event
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-10791">
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10792">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-10793">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-10794">
                  Card Transaction Fees
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-10795">
                <FixedFee label="Visa/Mastercard" sublabel="Per successful transaction" amount={0.5} data-id="element-10796" />
                <FixedFee label="American Express" sublabel="Per successful transaction" amount={0.75} data-id="element-10797" />
                <FixedFee label="Authorization Fee" sublabel="Per authorization attempt" amount={0.2} data-id="element-10798" />
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10799">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-10800">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-10801">
                  E-Wallet & Online Banking
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-10802">
                <FixedFee label="Touch 'n Go / GrabPay" sublabel="Per successful transaction" amount={0.3} data-id="element-10803" />
                <FixedFee label="FPX (Online Banking)" sublabel="Per successful transaction" amount={0.4} data-id="element-10804" />
                <FixedFee label="DuitNow QR" sublabel="Per successful transaction" amount={0.25} data-id="element-10805" />
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10806">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-10807">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-10808">
                Additional Service Fees
              </h4>
            </div>
            <table className="w-full text-left" data-id="element-10809">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10810">
                <tr data-id="element-10811">
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-10812">
                    Service
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-10813">
                    Description
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right" data-id="element-10814">
                    Fee
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10815">
                <tr data-id="element-10816">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10817">
                    Refund Processing
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-10818">
                    Per refund transaction
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-10819">
                    {formatCurrencyAmount(1.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-10820">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10821">
                    Chargeback Fee
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-10822">
                    Per chargeback filed
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-10823">
                    {formatCurrencyAmount(50.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-10824">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10825">
                    Chargeback Reversal
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-10826">
                    When chargeback is won
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-green-600 text-right" data-id="element-10827">{`-${formatCurrencyAmount(50.0, {
                    currency
                  })}`}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-10828">
            <div className="flex items-start gap-2" data-id="element-10829">
              <InfoIcon size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-10830" />
              <div className="text-sm text-gray-700" data-id="element-10831">
                <p className="font-medium text-gray-900 mb-1" data-id="element-10832">Fee Structure:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-10833">
                  <li data-id="element-10834">
                    <span className="font-medium" data-id="element-10835">Total cost</span> =
                    (Transaction Amount × MDR%) + Fixed Fee
                  </li>
                  <li data-id="element-10836">
                    Example: MYR 100 Visa on-us = (MYR 100 × 1.10%) + MYR 0.50 =
                    MYR 1.60
                  </li>
                  <li data-id="element-10837">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Fees */}
        <div className="border-b border-gray-200 pb-6" data-id="element-10838">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-10839">
            <DollarSignIcon size={16} className="mr-2 text-blue-600" data-id="element-10840" />
            Subscription Fees
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-10841">
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-10842">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10843">
                Annual Rental
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-10844">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10845">Billed annually</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-10846">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10847">
                Monthly Equivalent
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-10848">
                {formatCurrencyAmount(200, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10849">
                MYR 2,400 / 12 months
              </p>
            </div>
          </div>
        </div>

        {/* Last Payment */}
        <div data-id="element-10850">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-10851">
            <ClockIcon size={16} className="mr-2 text-blue-600" data-id="element-10852" />
            Last Subscription Payment
          </h3>
          <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-id="element-10853">
            <div data-id="element-10854">
              <p className="text-sm font-medium text-gray-900" data-id="element-10855">
                Annual Subscription Renewal
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10856">
                Payment Date: January 15, 2026
              </p>
            </div>
            <div className="text-right" data-id="element-10857">
              <p className="text-lg font-bold text-gray-900" data-id="element-10858">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1" data-id="element-10859">
                Paid
              </span>
            </div>
          </div>
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-10860">
            <p className="text-xs text-gray-600" data-id="element-10861">
              <span className="font-medium" data-id="element-10862">Next renewal:</span> January 15,
              2027
            </p>
          </div>
        </div>
      </div>
    </div>;
  const renderActivityLogs = () => {
    const filteredLogs = activityLogs.filter(log => {
      if (logFilters.search && !log.description.toLowerCase().includes(logFilters.search.toLowerCase()) && !log.user.name.toLowerCase().includes(logFilters.search.toLowerCase())) return false;
      if (logFilters.user !== 'all' && log.user.id !== logFilters.user) return false;
      if (logFilters.section !== 'all' && log.section !== logFilters.section) return false;
      if (logFilters.dateRange !== 'all') {
        const logDate = new Date(log.timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24));
        if (logFilters.dateRange === '24h' && daysDiff > 1) return false;
        if (logFilters.dateRange === '7days' && daysDiff > 7) return false;
        if (logFilters.dateRange === '30days' && daysDiff > 30) return false;
      }
      return true;
    });
    const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
    const paginatedLogs = filteredLogs.slice((currentLogPage - 1) * logsPerPage, currentLogPage * logsPerPage);
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-10863">
        <div data-id="element-10864">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10865">
            Activity Logs
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10866">
            View and track all user activities and system events
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10867">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10868">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10869">
              <SearchIcon size={18} className="text-gray-600" data-id="element-10870" />
              Filters
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-10871">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-10872">
              <div data-id="element-10873">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10874">
                  Search
                </label>
                <input type="text" placeholder="Search logs..." className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.search} onChange={e => setLogFilters({
                ...logFilters,
                search: e.target.value
              })} data-id="element-10875" />
              </div>
              <div data-id="element-10876">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10877">
                  Date Range
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.dateRange} onChange={e => setLogFilters({
                ...logFilters,
                dateRange: e.target.value
              })} data-id="element-10878">
                  <option value="all" data-id="element-10879">All Time</option>
                  <option value="24h" data-id="element-10880">Last 24 Hours</option>
                  <option value="7days" data-id="element-10881">Last 7 Days</option>
                  <option value="30days" data-id="element-10882">Last 30 Days</option>
                </select>
              </div>
              <div data-id="element-10883">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10884">
                  User
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.user} onChange={e => setLogFilters({
                ...logFilters,
                user: e.target.value
              })} data-id="element-10885">
                  <option value="all" data-id="element-10886">All Users</option>
                  {users.map(u => <option key={u.id} value={u.id} data-id="element-10887">
                      {u.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-10888">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10889">
                  Section
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.section} onChange={e => setLogFilters({
                ...logFilters,
                section: e.target.value
              })} data-id="element-10890">
                  <option value="all" data-id="element-10891">All Sections</option>
                  <option value="Payments" data-id="element-10892">Payments</option>
                  <option value="Customers" data-id="element-10893">Customers</option>
                  <option value="Invoices" data-id="element-10894">Invoices</option>
                  <option value="Bills" data-id="element-10895">Bills</option>
                  <option value="Refunds" data-id="element-10896">Refunds</option>
                  <option value="Users" data-id="element-10897">Users</option>
                  <option value="Settings" data-id="element-10898">Settings</option>
                  <option value="Security" data-id="element-10899">Security</option>
                </select>
              </div>
              <div data-id="element-10900">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10901">
                  Per Page
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logsPerPage} onChange={e => {
                setLogsPerPage(Number(e.target.value));
                setCurrentLogPage(1);
              }} data-id="element-10902">
                  <option value="25" data-id="element-10903">25</option>
                  <option value="50" data-id="element-10904">50</option>
                  <option value="100" data-id="element-10905">100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2" data-id="element-10906">
              <p className="text-sm text-gray-600" data-id="element-10907">
                Showing {paginatedLogs.length} of {filteredLogs.length} logs
              </p>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10908">
          <div className="overflow-x-auto" data-id="element-10909">
            <table className="w-full" data-id="element-10910">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10911">
                <tr data-id="element-10912">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10913">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10914">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10915">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10916">
                    Section
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10917">
                    Object ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10918">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10919">
                {paginatedLogs.map(log => <tr key={log.id} className="hover:bg-gray-50 transition-colors" data-id="element-10920">
                    <td className="px-6 py-4 text-sm text-gray-900" title={new Date(log.timestamp).toLocaleString()} data-id="element-10921">
                      {getRelativeTime(log.timestamp)}
                    </td>
                    <td className="px-6 py-4" data-id="element-10922">
                      <div className="flex items-center gap-2" data-id="element-10923">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold" data-id="element-10924">
                          {log.user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div data-id="element-10925">
                          <p className="text-sm font-medium text-gray-900" data-id="element-10926">
                            {log.user.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-10927">
                            {log.user.role}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-10928">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-10929">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" data-id="element-10930">
                      {log.section}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-10931">
                      {log.objectId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-10932">
                      {log.ipAddress}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-10933">
              <button onClick={() => setCurrentLogPage(Math.max(1, currentLogPage - 1))} disabled={currentLogPage === 1} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10934">
                Previous
              </button>
              <span className="text-sm text-gray-600" data-id="element-10935">
                Page {currentLogPage} of {totalPages}
              </span>
              <button onClick={() => setCurrentLogPage(Math.min(totalPages, currentLogPage + 1))} disabled={currentLogPage === totalPages} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10936">
                Next
              </button>
            </div>}
        </div>
      </div>;
  };
  const renderRequests = () => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'Closed':
          return 'bg-gray-100 text-gray-800';
        case 'Pending':
          return 'bg-amber-100 text-amber-800';
        case 'In Progress':
          return 'bg-blue-100 text-blue-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-10937">
        <div data-id="element-10938">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10939">
            Merchant Requests
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10940">
            View and track requests for payment method activations and custom
            user roles
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10941">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10942">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10943">
              <ClipboardListIcon size={18} className="text-gray-600" data-id="element-10944" />
              All Requests ({merchantRequests.length})
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-10945">
            <table className="w-full" data-id="element-10946">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10947">
                <tr data-id="element-10948">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10949">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10950">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10951">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10952">
                    Requested By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10953">
                    Requested At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10954">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10955">
                {merchantRequests.map(request => <tr key={request.id} className="hover:bg-gray-50 transition-colors" data-id="element-10956">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 font-mono" data-id="element-10957">
                      {request.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-10958">
                      {request.type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs" data-id="element-10959">
                      {request.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-10960">
                      {request.requestedBy}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" title={new Date(request.requestedAt).toLocaleString()} data-id="element-10961">
                      {getRelativeTime(request.requestedAt)}
                    </td>
                    <td className="px-6 py-4" data-id="element-10962">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-id="element-10963">
                        {request.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10964">
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10965">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10966">
              Total Requests
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-10967">
              {merchantRequests.length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10968">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10969">
              Pending
            </p>
            <p className="text-2xl font-bold text-amber-600" data-id="element-10970">
              {merchantRequests.filter(r => r.status === 'Pending').length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10971">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10972">
              Closed
            </p>
            <p className="text-2xl font-bold text-gray-600" data-id="element-10973">
              {merchantRequests.filter(r => r.status === 'Closed').length}
            </p>
          </div>
        </div>
      </div>;
  };
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'company':
        return renderCompanySettings();
      case 'branding':
        return renderBrandingSettings();
      case 'general':
        return renderGeneralSettings();
      case 'receipt':
        return renderReceiptSettings();
      case 'payment-providers':
        return renderPaymentProviders();
      case 'users':
        return renderUsers();
      case 'payout':
        return renderPayoutSettings();
      case 'fields':
        return renderFields();
      case 'notifications':
        return renderNotifications();
      case 'security':
        return renderSecurity();
      case 'subscription-plan':
        return renderSubscriptionPlanSettings();
      default:
        return null;
    }
  };
  return <div className="max-w-7xl mx-auto py-4 sm:py-8 px-4" data-id="element-10974">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8" data-id="element-10975">
        Settings
      </h1>
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6" data-id="element-10976">
        <nav className="lg:w-64 flex-shrink-0" data-id="element-10977">
          <div className="lg:hidden mb-4" data-id="element-10978">
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-10979">
              {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${isActive ? 'bg-[#E2231A] text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`} data-id="element-10980">
                    <Icon size={16} data-id="element-10981" />
                    <span data-id="element-10982">{tab.label}</span>
                  </button>;
            })}
            </div>
          </div>
          <div className="hidden lg:block" data-id="element-10983">
            <div className="space-y-6" data-id="element-10984">
              {categories.map(category => <div key={category.id} data-id="element-10985">
                  <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-10986">
                    {category.label}
                  </h3>
                  <ul className="space-y-1" data-id="element-10987">
                    {tabs.filter(tab => tab.category === category.id).map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return <li key={tab.id} data-id="element-10988">
                            <button onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-[#E2231A] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-10989">
                              <Icon size={16} data-id="element-10990" />
                              <span data-id="element-10991">{tab.label}</span>
                            </button>
                          </li>;
                })}
                  </ul>
                </div>)}
            </div>
          </div>
        </nav>
        <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 sm:p-6 overflow-x-auto" data-id="element-10992">
          {renderActiveTab()}
        </div>
      </div>
    </div>;
}